/**
 * Option Snapshot Domain Model
 * Types and mapping logic for option snapshot data from Massive API
 * No network dependencies - pure types and transformation logic
 */

// ============================================================================
// Domain Types
// ============================================================================

export type OptionSnapshotResult =
  | { ok: true; requestId?: string; contractSymbol: string; snapshot: OptionSnapshot }
  | { ok: false; requestId?: string; contractSymbol: string; error: OptionSnapshotError };

export interface OptionSnapshot {
  asOf?: string | null; // ISO date-time if available, else null
  underlying?: string | null; // if Massive provides it
  quote: OptionSnapshotQuote;
  greeks: OptionSnapshotGreeks | null;
  iv: OptionSnapshotIv | null;
  volume: OptionSnapshotVolume | null;
  openInterest: OptionSnapshotOpenInterest | null;
  source: "massive";
}

export interface OptionSnapshotQuote {
  bid: number | null;
  ask: number | null;
  last: number | null;
  mid: number | null; // derived when bid/ask present
  change: number | null; // day change if available
  changePercent: number | null; // day change percent if available
  bidSize: number | null;
  askSize: number | null;
  lastSize: number | null;
  timestamp?: string | null; // if available
}

export interface OptionSnapshotGreeks {
  delta: number | null;
  gamma: number | null;
  theta: number | null;
  vega: number | null;
  rho: number | null;
}

export interface OptionSnapshotIv {
  impliedVolatility: number | null; // as decimal (0.25 = 25%)
}

export interface OptionSnapshotVolume {
  value: number | null;
}

export interface OptionSnapshotOpenInterest {
  value: number | null;
}

export interface OptionSnapshotError {
  code: "auth" | "rate_limited" | "not_found" | "bad_request" | "backend" | "network" | "unknown";
  message: string;
  details?: Record<string, unknown>;
}

// ============================================================================
// Proxy Response Types
// ============================================================================

export interface ProxyOk<T> {
  ok: true;
  request_id?: string;
  data: T;
}

export interface MassiveOptionSnapshotResponse {
  results?: MassiveSnapshotResults;
  request_id?: string;
}

export interface MassiveSnapshotResults {
  day?: {
    last_updated?: number | string;
    close?: number;
    volume?: number;
    change?: number;
    change_percent?: number;
  };
  last_quote?: {
    bid?: number;
    ask?: number;
    bid_size?: number;
    ask_size?: number;
  };
  last_trade?: {
    price?: number;
    size?: number;
    sip_timestamp?: number | string;
    participant_timestamp?: number | string;
  };
  greeks?: {
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
    rho?: number;
  };
  implied_volatility?: number;
  open_interest?: number;
  underlying_asset?: {
    ticker?: string;
  };
}

// ============================================================================
// Timestamp Conversion
// ============================================================================

/**
 * Convert epoch timestamp to ISO string
 * Handles seconds, milliseconds, microseconds, and nanoseconds
 */
function convertEpochToIso(value: number | string | null | undefined): string | null {
  if (value == null) return null;
  const num = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(num) || num <= 0) return null;

  let ms: number;
  if (num < 1e12) {
    // Seconds (rare)
    ms = num * 1000;
  } else if (num < 1e15) {
    // Milliseconds (1e12 to 1e14)
    ms = num;
  } else if (num < 1e18) {
    // Microseconds (1e15 to 1e17)
    ms = num / 1e3;
  } else {
    // Nanoseconds (>= 1e18)
    ms = num / 1e6;
  }

  return new Date(ms).toISOString();
}

// ============================================================================
// Mapping Function
// ============================================================================

/**
 * Map Massive API response to domain model
 * @param raw - Raw proxy response (must have ok: true and data.results)
 * @param contractSymbol - Original contract symbol for error context
 * @param requestId - Request ID from proxy response
 * @returns OptionSnapshotResult
 */
export function mapMassiveOptionSnapshot(
  raw: ProxyOk<MassiveOptionSnapshotResponse>,
  contractSymbol: string,
  requestId?: string
): OptionSnapshotResult {
  // Check for bad response structure
  if (!raw.data?.results) {
    return {
      ok: false,
      requestId: requestId || raw.request_id,
      contractSymbol,
      error: {
        code: "backend",
        message: "Bad proxy response: missing data.results",
      },
    };
  }

  const results = raw.data.results;
  const day = results.day || {};
  const lastQuote = results.last_quote;
  const lastTrade = results.last_trade;
  const greeks = results.greeks;

  // Convert timestamp
  const asOf = day.last_updated ? convertEpochToIso(day.last_updated) : null;

  // Build quote
  const bid = lastQuote?.bid ?? null;
  const ask = lastQuote?.ask ?? null;
  const last = lastTrade?.price ?? day.close ?? null;
  const mid = bid !== null && ask !== null ? (bid + ask) / 2 : null;

  // Quote sizes (only if present)
  const bidSize = lastQuote?.bid_size ?? null;
  const askSize = lastQuote?.ask_size ?? null;
  const lastSize = lastTrade?.size ?? null;

  // Quote timestamp (prefer sip_timestamp, fallback to participant_timestamp)
  const quoteTimestamp =
    lastTrade?.sip_timestamp || lastTrade?.participant_timestamp
      ? convertEpochToIso(lastTrade.sip_timestamp || lastTrade.participant_timestamp)
      : null;

  const quote: OptionSnapshotQuote = {
    bid,
    ask,
    last,
    mid,
    change: day.change ?? null,
    changePercent: day.change_percent ?? null,
    bidSize,
    askSize,
    lastSize,
    timestamp: quoteTimestamp || undefined,
  };

  // Build greeks
  const snapshotGreeks: OptionSnapshotGreeks | null = greeks
    ? {
        delta: greeks.delta ?? null,
        gamma: greeks.gamma ?? null,
        theta: greeks.theta ?? null,
        vega: greeks.vega ?? null,
        rho: greeks.rho ?? null,
      }
    : null;

  // Build IV
  const iv: OptionSnapshotIv | null = results.implied_volatility != null
    ? {
        impliedVolatility: results.implied_volatility,
      }
    : null;

  // Build volume
  const volume: OptionSnapshotVolume | null = day.volume != null
    ? {
        value: day.volume,
      }
    : null;

  // Build open interest
  const openInterest: OptionSnapshotOpenInterest | null = results.open_interest != null
    ? {
        value: results.open_interest,
      }
    : null;

  // Build snapshot
  const snapshot: OptionSnapshot = {
    asOf,
    underlying: results.underlying_asset?.ticker ?? null,
    quote,
    greeks: snapshotGreeks,
    iv,
    volume,
    openInterest,
    source: "massive",
  };

  return {
    ok: true,
    requestId: requestId || raw.request_id,
    contractSymbol,
    snapshot,
  };
}
